import { CalendarDays, ChevronLeft, ChevronRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { RANGE_PRESETS, rangeLabel, type DisplayRange, type RangePreset } from '@/lib/taskDateRange'

/**
 * Date scope for the Tasks worklist.
 *
 * The range shown here is INCLUSIVE at both ends, because that is what "1 Aug →
 * 31 Aug" means to a reader. Conversion to the half-open window both endpoints
 * want lives in `toQueryRange` — never do it here.
 */
export function TaskDateRange({
  preset,
  range,
  isCustom,
  onPreset,
  onShift,
  onCustom,
}: {
  preset: RangePreset
  range: DisplayRange
  /** True when the range was typed rather than picked, so no preset is active. */
  isCustom: boolean
  onPreset: (preset: RangePreset) => void
  onShift: (direction: 1 | -1) => void
  onCustom: (from: string, to: string) => void
}) {
  // "All dates" has nothing to step through.
  const canShift = range !== null

  return (
    <div className='flex flex-wrap items-center gap-2'>
      <div className='flex items-center gap-1'>
        <button
          type='button'
          onClick={() => onShift(-1)}
          disabled={!canShift}
          aria-label='Previous period'
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors',
            canShift ? 'hover:bg-gray-50 hover:text-gray-900' : 'cursor-not-allowed opacity-40',
          )}
        >
          <ChevronLeft className='h-4 w-4' />
        </button>
        <button
          type='button'
          onClick={() => onShift(1)}
          disabled={!canShift}
          aria-label='Next period'
          className={cn(
            'inline-flex h-8 w-8 items-center justify-center rounded-md border border-gray-200 text-gray-500 transition-colors',
            canShift ? 'hover:bg-gray-50 hover:text-gray-900' : 'cursor-not-allowed opacity-40',
          )}
        >
          <ChevronRight className='h-4 w-4' />
        </button>
      </div>

      <span className='inline-flex min-w-[150px] items-center gap-1.5 text-sm font-semibold text-gray-900'>
        <CalendarDays className='h-4 w-4 shrink-0 text-gray-400' />
        {rangeLabel(preset, range)}
      </span>

      <div className='flex items-center gap-1 rounded-lg border border-gray-200 bg-gray-50 p-0.5'>
        {RANGE_PRESETS.map((p) => (
          <button
            key={p.id}
            type='button'
            onClick={() => onPreset(p.id)}
            className={cn(
              'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
              !isCustom && preset === p.id
                ? 'bg-primary text-primary-foreground shadow-sm'
                : 'text-gray-600 hover:bg-white hover:text-gray-900',
            )}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Explicit dates, for a scope no preset covers. Hidden on "All dates",
          where there is no range to edit. */}
      {range && (
        <div
          className={cn(
            'flex items-center gap-1.5 rounded-lg border px-2 py-1',
            isCustom ? 'border-primary/40 bg-primary/5' : 'border-gray-200',
          )}
        >
          <input
            type='date'
            aria-label='From date'
            value={range.from}
            max={range.to}
            onChange={(e) => e.target.value && onCustom(e.target.value, range.to)}
            className='w-[120px] bg-transparent text-xs text-gray-700 outline-none'
          />
          <span className='text-xs text-gray-400'>→</span>
          <input
            type='date'
            aria-label='To date'
            value={range.to}
            min={range.from}
            onChange={(e) => e.target.value && onCustom(range.from, e.target.value)}
            className='w-[120px] bg-transparent text-xs text-gray-700 outline-none'
          />
        </div>
      )}
    </div>
  )
}
